import React, { useEffect, useState } from "react";
import { Button, Form, Header, Message, Segment, Dropdown } from "semantic-ui-react";
import { toast } from "react-toastify";
import { ToasterConfig } from "../../../config/ToasterConfig";
import useRouteService from "../../../services/backoffice/hooks/useRouteService";
import { CreateRouteCommand } from "../../../models/requests/create-route-command";
import { UpdateRouteCommand } from "../../../models/requests/update-route-command";
import usePointOfInterestService from "../../../services/backoffice/hooks/usePointOfInterestService";
import { ShortDto } from "../../../models/short-dto";
import useReferenceService from "../../../services/backoffice/hooks/useReferenceService";

interface RouteFormProps {
    routeId?: string;
    onClose?: () => void;
    isModal?: boolean;
    open?: boolean;
}

const RouteForm: React.FC<RouteFormProps> = ({ routeId, onClose }) => {
    const [route, setRoute] = useState<Partial<CreateRouteCommand>>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const { createRoute, updateRoute, getRouteById } = useRouteService();
    const { getReferencesShort } = useReferenceService(); // Chamar o hook

    const [references, setReferences] = useState<ShortDto[]>([]); // Estado para referências



    useEffect(() => {
        async function loadReferences() {
            try {
                const refs = await getReferencesShort(); // Chamar a nova função
                setReferences(refs);
            } catch (err) {
                setError("Erro ao carregar referências.");
            }
        }
        loadReferences();
    }, []);

    useEffect(() => {
        if (routeId) {
            setLoading(true);
            getRouteById(routeId)
                .then((response) => {
                    setRoute({
                        originId: response.originId,
                        destinationId: response.destinationId
                    });
                })
                .catch(() => {
                    setError("Erro ao carregar a rota.");
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [routeId]);

    const handleSubmit = async () => {
        try {
            if (!route.originId || !route.destinationId) {
                setError("Todos os campos são obrigatórios.");
                return;
            }

            if (route.originId === route.destinationId) {
                setError("Origem e destino não podem ser iguais.");
                return;
            }

            setLoading(true);
            const command: CreateRouteCommand | UpdateRouteCommand = {
                originId: route.originId,
                destinationId: route.destinationId
            };

            if (routeId) {
                await updateRoute(routeId, command as UpdateRouteCommand);
                toast.success("Rota atualizada com sucesso", ToasterConfig.success);
            } else {
                await createRoute(command);
                toast.success("Rota criada com sucesso", ToasterConfig.success);
            }

            if (onClose) onClose();
        } catch {
            setError("Erro ao salvar a rota.");
        } finally {
            setLoading(false);
        }
    };

    const handleDropdownChange = (field: keyof CreateRouteCommand) => (event: React.SyntheticEvent, data: any) => {
        setRoute((prev) => ({ ...prev, [field]: data.value }));
    };

    const handleClear = () => {
        setRoute({});
    };

    const isSubmitDisabled = !route.originId || !route.destinationId;

    const pointsOptions = references.map(point => ({
        key: point.key,
        text: point.text,
        value: point.value
    }));

    return (
        <Segment raised style={{ maxWidth: "600px", margin: "5px auto", padding: "20px" }}>
            <Header
                as="h3"
                style={{
                    background: "#49ab3a",
                    color: "#fff",
                    display: "flex",
                    alignItems: "left",
                    justifyContent: "space-between",
                    padding: "10px",
                    margin: "0",
                    borderRadius: "5px 5px 0 0",
                }}
            >
                {routeId ? "Editar Rota" : "Cadastro de Rota"}
            </Header>
            <Form onSubmit={handleSubmit}>
                <div style={{ border: "2px solid #d4d4d5", borderRadius: "4px", padding: "10px", margin: "10px 0" }}>
                    <Form.Field required>
                        <label>Origem</label>
                        <Dropdown
                            fluid
                            search
                            selection
                            options={references}
                            placeholder="Selecione a origem"
                            value={route.originId || ""}
                            onChange={handleDropdownChange("originId")}
                            disabled={loading}
                            error={isSubmitDisabled && !route.originId}
                        />
                    </Form.Field>
                    <Form.Field required>
                        <label>Destino</label>
                        <Dropdown
                            fluid
                            search
                            selection
                            options={references}
                            placeholder="Selecione o destino"
                            value={route.destinationId || ""}
                            onChange={handleDropdownChange("destinationId")}
                            disabled={loading}
                            error={isSubmitDisabled && !route.destinationId}
                        />
                    </Form.Field>
                </div>
                <div style={{ textAlign: "right" }}>
                    <Button color="green" type="submit" disabled={isSubmitDisabled || loading}>
                        {routeId ? "Atualizar" : "Salvar"}
                    </Button>
                    <Button type="button" onClick={handleClear} disabled={loading}>
                        Limpar
                    </Button>
                    <Button type="button" onClick={onClose} disabled={loading}>
                        Cancelar
                    </Button>
                </div>
                {error && <Message negative>{error}</Message>}
            </Form>
        </Segment>
    );
};

export default RouteForm; 